import React, { useState, useEffect, useRef } from 'react';
import { Box, FormControlLabel, FormHelperText, Switch, Typography } from '@mui/material';
import { ConstantActions, FlowBuilderActions } from '../../../../Services/_actions';
import SweetAlert from '../../../Shared/Notification';
import { botFlowDataAtom, flowObjAtom, flowValidationAtom } from '../../../../Services/_states';
import { useRecoilValue } from 'recoil';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import CloseIcon from '@mui/icons-material/Close';
import LoadingWrapper from '../../../Shared/LoadingWrapper/index.jsx';
import RenderTextArea from './RenderTextArea.tsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import { getAllParentNodeResults } from '../../helper.tsx';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';

interface SuggetionObj {
  label: string;
  value: string;
}
const AudioNode = ({ data }) => {
  const actions = FlowBuilderActions();
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const { showSnackBar } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const botFlowData = useRecoilValue(botFlowDataAtom);

  // State for body text
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(['Option 1', 'Option 2', 'Option 3']);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update the bodyText state when the data.body prop changes
  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      // const query = match[1]; // Get the text after `{{@`
      // setFilteredSuggestions(suggestions.filter((s) =>s.label.toLowerCase().includes(query.toLowerCase())));
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // Update `params` by finding all valid `{{label}}` entries in the text
    const matches = value.match(/{{(.*?)}}/g); // Match all `{{label}}`
    const labelsInBody = matches ? matches.map((match) => match.replace(/{{|}}/g, '')) : [];

    // Categorize params by `paramType`
    const categorizedParams = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggetionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === 'global') {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === 'result') {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === 'webform') {
          categorizedParams.webform.push(result.value);
        }
      }
    });

    // Update parent data
    data.onChange(data.id, { body: value, params: categorizedParams });

    // Adjust the textarea height to fit content
    // if (textareaRef.current) {
    //   textareaRef.current.style.height = "auto"; // Reset height
    //   textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12
    //     }px`; // Set height to content
    // }
  };

  const handleSuggestionClick = (suggestion: SuggetionObj) => {
    if (cursorPosition !== null) {
      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);
      const updatedText =
        textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) + textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      // Initialize parameters if not present
      const updatedParams = {
        global: data.params?.global || [],
        result: data.params?.result || [],
        webform: data.params?.webform || [],
      };

      // Add the value to the correct array based on paramType
      if (suggestion.paramType === 'global') {
        updatedParams.global = [...updatedParams.global, suggestion.value];
      } else if (suggestion.paramType === 'result') {
        updatedParams.result = [...updatedParams.result, suggestion.value];
      } else if (suggestion.paramType === 'webform') {
        updatedParams.webform = [...updatedParams.webform, suggestion.value];
      }

      // Update parent data
      data.onChange(data.id, { body: updatedText, params: updatedParams });

      // Move cursor to the end of the inserted suggestion
      // setTimeout(() => {
      //   textareaRef.current?.setSelectionRange(
      //     updatedText.length,
      //     updatedText.length
      //   );
      // }, 0);
    }
  };

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleAudioUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const ext = fileName.split('.').pop();

    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      event.target.value = '';
      return;
    }

    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      event.target.value = '';
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
        'error'
      );
      event.target.value = '';
      return;
    }
    // Validate file type
    const validAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['mp3', 'wav', 'mpeg'];

    // Check both MIME type and file extension
    if (!validAudioTypes.includes(file.type)) {
      showSnackBar('Only audio files (MP3, WAV, ) are allowed.', 'error');
      event.target.value = ''; // Clear the file input
      return;
    }

    // Double-check with file extension
    if (fileExtension && !validExtensions.includes(fileExtension)) {
      showSnackBar('Invalid file extension. Only MP3, WAV are allowed.', 'error');
      event.target.value = ''; // Clear the file input
      return;
    }

    // Validate file size (max 16MB for WhatsApp)
    const MAX_AUDIO_SIZE = 16 * 1024 * 1024; // 16 MB
    if (file.size > MAX_AUDIO_SIZE) {
      showSnackBar('Audio size must be less than 16 MB.', 'error');
      event.target.value = ''; // Clear the file input
      return;
    }

    setIsLoading(true);
    try {
      let formData = new FormData();
      formData.append('bot_number', botFlowData?.phone_number_id || '');
      formData.append('uploaded_file', file);
      let resp = await actions.uploadMediaToWhatsapp(formData);

      if (resp?.status === 200) {
        let { messaging_product, ...updatedData } = resp?.data;
        data.onChange(data.id, { media: updatedData });
        showSnackBar('Audio uploaded successfully!', 'success');
      } else {
        showSnackBar(resp?.response?.data?.detail || 'Error uploading audio', 'error');
      }
    } catch (error) {
      showSnackBar('Error uploading audio', 'error');
      console.error('Upload error:', error);
    } finally {
      setIsLoading(false);
      event.target.value = ''; // Clear the file input after processing
    }
  };

  // const handleAudioUpload = async (event) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //      const validImageTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3'];

  //   if (!validImageTypes.includes(file.type)) {
  //     showSnackBar('Only audio files (mpeg, wav, mp3) are allowed.', 'error');
  //     return;
  //   }
  //     // Validate audio file size (e.g., max 5 MB)
  //     const MAX_AUDIO_SIZE = 20 * 1024 * 1024; // 5 MB
  //     if (file.size > MAX_AUDIO_SIZE) {
  //       showSnackBar(`Audio file size must be less than 5 MB.`, 'error');
  //       return;
  //     }

  //     // Validate audio file type
  //     const validAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
  //     if (!validAudioTypes.includes(file.type)) {
  //       showSnackBar("Invalid audio type. Accepted types are MP3, WAV, and OGG.", 'error');
  //       return;
  //     }
  //     setIsLoading(true);
  //     let formData = new FormData();
  //     formData.append('bot_number', botFlowData?.phone_number_id || "");
  //     formData.append("uploaded_file", file);
  //     let resp = await actions.uploadMediaToWhatsapp(formData);
  //     if (resp?.status === 200) {
  //       let { messaging_product, ...updatedData } = resp?.data;
  //       data.onChange(data.id, { media: updatedData });
  //       showSnackBar('Audio uploaded successfully!', "success");
  //     } else {
  //       showSnackBar(resp?.response?.data?.detail || 'Error', "error");
  //     }
  //     setIsLoading(false);

  //     // Convert audio to base64 or handle upload logic
  //     // const reader = new FileReader();
  //     // reader.onload = () => {
  //     //   data.onChange(data.id, { url: reader.result });
  //     // };
  //     // reader.readAsDataURL(file);
  //   }
  // };

  return (
    <div>
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => {
                const enabled = e.target.checked;

                if (!enabled) {
                  data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                } else {
                  data.onChange(data.id, { ai_mode_enabled: true });
                }
              }}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        />
        {data.ai_mode_enabled && (
          <SearchableDropdown
            options={parentResults.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            multiSelect={true}
            onChange={(selectedOptions) => {
              const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
              data.onChange(data.id, { ai_query_parameters: selectedValues });
            }}
            value={parentResults
              .map((item) => ({ label: item.label, value: item.value }))
              .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
          />
        )}
      </Box>
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Header:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.header || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue.length <= 60) {
                data.onChange(data.id, { header: newValue });
              }
            }}
            placeholder="Enter Header Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px', // Reserve space for character count
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.header?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.header || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.header && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.header}
          </Typography>
        )}
      </div>

      {/* <label>Body:</label>
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={bodyText}
          onChange={handleBodyChange}
          placeholder="Enter text"
          maxLength={4096}
          style={{
            width: '100%',
            height: 'auto',
            padding: '5px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f5f5f5',
            overflow: 'hidden',
            resize: 'none',
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: '10px',
            bottom: '10px',
            fontSize: '12px',
            color: bodyText.length === 4096 ? 'red' : '#888',
          }}
        >
          {`${bodyText.length}/4096`}
        </span>
      </div> */}
      {/* {bodyText?.length === 500 && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          Maximum 500 characters allowed.
        </Typography>
      )} */}

      {/* Suggestions Dropdown */}
      {/* {showSuggestions && data?.parentResults.length > 0 && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            zIndex: 1000,
            width: '100%',
          }}
        >
          {data?.parentResults?.map((suggestion: SuggetionObj, index: Number) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '5px 10px',
                cursor: 'pointer',
                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
              }}
            >
              {suggestion?.label}
            </div>
          ))}
        </div>
      )} */}
      <RenderTextArea data={data} />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Footer:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.footer || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue.length <= 60) {
                data.onChange(data.id, { footer: newValue });
              }
            }}
            placeholder="Enter Footer Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px', // Reserve space for character count
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.footer?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.footer || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.footer && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.footer}
          </Typography>
        )}
      </div>

      {data.type === 'audio' && (
        <>
          {data?.media?.s3_object?.url ? (
            <>
              {data?.media?.s3_object?.filename}
              <CloseIcon
                style={{
                  position: 'absolute',
                  // top: '5px',
                  right: '5px',
                  color: 'red',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
                onClick={() => data.onChange(data.id, { media: '' })}
              />
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: '4px',
                  marginTop: '10px',
                }}
              >
                <audio
                  controls
                  src={data?.media?.s3_object?.url}
                  style={{ width: '100%', marginTop: '10px', borderRadius: '4px' }}
                />
              </div>
            </>
          ) : (
            <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
              {isLoading ? (
                <LoadingWrapper />
              ) : (
                <>
                  <label>Upload Audio:</label>
                  {/* <input
                    type="file"
                    accept="audio/mpeg,audio/wav,audio/mp3"
                    onChange={handleAudioUpload}
                    style={{ width: '100%', marginBottom: '10px' }}
                  /> */}
                  <FileInput
                    onFileSelect={handleAudioUpload}
                    accept="audio/mpeg,audio/wav,audio/mp3"
                  />

                  <FormHelperText sx={{ mt: 0 }}>
                    Allowed: MP3, WAV, MPEG • Max 16 MB
                  </FormHelperText>
                </>
              )}
            </div>
          )}
        </>
      )}
      {flowValidationObj[data?.id]?.url && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.url}
        </Typography>
      )}
    </div>
  );
};

export default AudioNode;
